import {Component, ElementRef, EventEmitter, forwardRef, HostBinding, HostListener, Input, OnInit, Output, TemplateRef} from '@angular/core';
import {ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {CdkConnectedOverlay, CdkOverlayOrigin} from '@angular/cdk/overlay';

@Component({
  selector: 'core-select',
  imports: [CommonModule, FormsModule, CdkOverlayOrigin, CdkConnectedOverlay],
  templateUrl: './select.html',
  styleUrl: './select.scss',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => Select),
      multi: true
    }
  ],
  host: {
    class: 'select-control'
  }
})
export class Select implements ControlValueAccessor, OnInit {
  @Input() options: { label: string, value: any }[] = [];
  @Input() placeholder: string = 'Seleccionar';
  @Input() selectFirst: boolean = false;
  @Input() initialValue: any = null;
  @Input() multiple: boolean = false;
  @Input() placement: 'top' | 'bottom' = 'bottom';
  @Input() footerTemplate?: TemplateRef<any>;
  @Input() labelTemplate?: TemplateRef<any>;
  @Input() optionTemplate?: TemplateRef<any>;
  @Input() externalFilter: boolean = false;
  @Input() searchable: boolean = false;
  @Input() arrowClass: string = 'filter-neutral-900';
  @Input() disabled: boolean = false;
  @Output() onSearch = new EventEmitter<string>();
  selectedValue: any = null;
  isOpen: boolean = false;

  constructor(private elementRef: ElementRef) {}

  @HostBinding('class.is-open') get openClass() {
    return this.isOpen;
  }

  private _searchText: string = '';

  get searchText(): string {
    return this._searchText;
  }

  set searchText(value: string) {
    this._searchText = value;
    if (this.externalFilter) {
      this.onSearch.emit(value);
    }
  }

  get selectedOption() {
    return this.options.find(opt => opt.value === this.selectedValue);
  }

  get selectedOptions() {
    if (!this.multiple || !Array.isArray(this.selectedValue)) {
      return [];
    }

    return this.options.filter(opt => this.selectedValue.includes(opt.value));
  }

  get selectedLabel(): string {
    if (this.multiple) {
      return this.selectedOptions.length
        ? this.selectedOptions.map(option => option.label).join(', ')
        : this.placeholder;
    }
    return this.selectedOption ? this.selectedOption.label : this.placeholder;
  }

  get filteredOptions() {
    if (this.externalFilter) return this.options;
    return this.options.filter(opt =>
      opt.label.toLowerCase().includes(this.searchText.toLowerCase())
    );
  }

  ngOnInit() {
    if (this.multiple) {
      this.selectedValue = Array.isArray(this.initialValue) ? [...this.initialValue] : [];
    } else if (this.initialValue !== null) {
      this.selectedValue = this.initialValue;
    }
    else if (this.selectFirst && this.options.length > 0 && this.selectedValue === null) {
      const first = this.options[0];
      this.selectOption(first);
    }
  }

  toggleDropdown() {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.searchText = '';
    }
    if (!this.isOpen) this.onTouched();
  }

  selectOption(option: { label: string, value: any }) {
    if (this.multiple) {
      const current = Array.isArray(this.selectedValue) ? [...this.selectedValue] : [];
      const index = current.findIndex(value => value === option.value);
      if (index >= 0) {
        current.splice(index, 1);
      } else {
        current.push(option.value);
      }
      this.selectedValue = current;
      this.onChange([...current]);
      return;
    }

    this.selectedValue = option.value;
    this.onChange(this.selectedValue);
    this.isOpen = false;
  }

  isSelected(option: { label: string, value: any }): boolean {
    if (this.multiple) {
      return Array.isArray(this.selectedValue) && this.selectedValue.includes(option.value);
    }

    return option.value === this.selectedValue;
  }

  @HostListener('document:click', ['$event'])
  clickOut(event: MouseEvent) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }

  // --- Implementación de ControlValueAccessor ---
  writeValue(value: any): void {
    if (this.multiple) {
      this.selectedValue = Array.isArray(value) ? [...value] : [];
      return;
    }

    this.selectedValue = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.isOpen = false;
    }
  }

  private onChange: (value: any) => void = () => {};

  private onTouched: () => void = () => {};
}
